import { useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
} from '@product-framework/index';
import {
  useDataProvider,
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useLocalization,
  useSnackbar,
  useStore,
} from '@ui-framework/index';

import { useNavigate } from 'react-router-dom';
import { createSchema } from '../../schemas';
import validationSchema from './validations';

function CompanyCreate() {
  const { formatMessage } = useLocalization();
  const createFormRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, get, getApiUrl } = useDataProvider(provider);
  const [formErrors, setFormErrors] = useState(null);
  const store = useStore();
  const storeName = 'companyFormStore';

  const postApiUrl = `${getApiUrl()}/sxcl/v1/companies`;

  const preProcessRequestData = data => {
    const { status, ...other } = data;
    return {
      status: status === 'true',
      ...other,
    };
  };

  const submitCreateForm = async data => {
    setFormErrors(null);
    const requestData = preProcessRequestData(data);
    store.uiState[storeName].set(requestData);
    const response = await post(postApiUrl, { ...requestData });
    if (response?.ok) {
      store.uiState[storeName].set({});
      snackbar.showMessage(
        formatMessage({
          id: 'COMPANY_CREATE_SUCCESS',
          defaultMessage: 'Company was saved successfully',
        }),
      );
      createFormRef.current?.resetAndNavigate(
        `/companies/edit/${response?.data?.id}`,
      );
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'COMPANY_CREATE_FAILURE',
          defaultMessage: 'Error: Could not save the company',
        }),
      });
    }
  };

  const formProps = {
    customComponents: FormFields,
    schema: createSchema,
    onSubmit: submitCreateForm,
    ref: createFormRef,
    initialValues: store.uiState[storeName].get(),
    validationSchema: validationSchema(formatMessage, getApiUrl, get),
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'COMPANIES_BREADCRUMB',
        defaultMessage: 'Companies',
      }),
      icon: IconLibrary.Companies,
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/companies');
      },
    },
    {
      name: formatMessage({
        id: 'CREATE_COMPANY_BREADCRUMB',
        defaultMessage: 'Create Company',
      }),
      icon: IconLibrary.Company,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'EDIT_COMPANIES_STACKABLE_BUTTON',
        defaultMessage: 'List Companies',
      }),
      Icon: IconLibrary.Companies,
      variant: 'outlined',
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/companies');
      },
    },
  ];

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <PageSection
        label={formatMessage({
          id: 'COMPANY_CREATE_HEADING',
          defaultMessage: 'Create Company',
        })}>
        <Box>
          <CRUDComponent
            errors={formErrors}
            type={CRUD_COMPONENT_TYPE.FORM}
            formProps={formProps}>
            <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
              <Button
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => createFormRef.current?.submit()}>
                {formatMessage({
                  id: 'COMPANY_CREATE_SAVE_BUTTON',
                  defaultMessage: 'Save Company',
                })}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => createFormRef.current?.reset()}>
                {formatMessage({
                  id: 'COMPANY_CREATE_RESET_BUTTON',
                  defaultMessage: 'Reset',
                })}
              </Button>
            </Box>
          </CRUDComponent>
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(CompanyCreate);
